import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

/**
 * Calls `onVisible` the first time the referenced element enters the viewport,
 * then stops observing.
 *
 * Impressions are counted once per render of a surface, so the latch is the
 * point: an element scrolled away from and back to has not been seen twice.
 *
 * `@noths/polaris-client-react-hooks` has `useImpressionRef`, which does the
 * same job for the web apps. This is deliberately a local copy rather than a new
 * dependency: the design system should not need a client-side package to
 * observe its own elements, and the carousel needs two more observers of its own
 * besides this one.
 */
export const useOnVisible = (
  ref: RefObject<Element>,
  onVisible: (() => void) | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  /* Held in a ref so that a consumer passing an inline arrow function, which
     almost all of them do, does not tear down and re-create the observer on
     every render. */
  const callback = useRef(onVisible);
  callback.current = onVisible;

  useEffect(() => {
    const element = ref.current;

    if (!element || !enabled || !callback.current) {
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        callback.current?.();
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [enabled, ref]);
};
